import React from "react";
import { STATIC_IP_STATUS, ORDER_STATUS } from "../../Util/key_helper";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { renderToStaticMarkup } from 'react-dom/server';

export const WithTooltip = ({ tooltipText, placement = 'top', children, show = true }) => {
    const tooltipId = React.useId();
    if (!show) {
        return children; // Return children directly if tooltip shouldn't be shown
    }
    return (
        <>
            <span data-tooltip-id={tooltipId} data-tooltip-content={tooltipText} data-tooltip-html={renderToStaticMarkup(tooltipText)}>
                {children}
            </span>
            <ReactTooltip id={tooltipId} event="click" place={placement} className="custom-tooltip" style={{ overflow: 'visible' }} />
        </>
    );
};
export function getTruncatedText(text, maxLength = 20, className = "", tag = "div") {
    const isTruncated = text?.length > maxLength;
    const truncatedText = isTruncated ? `${text.substring(0, maxLength)}...` : text;
    const CustomTag = tag;
    return (
        <WithTooltip tooltipText={text}>
            <CustomTag
                className={` ${className}`}
                style={{
                    whiteSpace: "warp",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                }}
            >
                {truncatedText}
            </CustomTag>
        </WithTooltip>
    );
}

// Convert number into K (thousands), M (millions), B (billions), etc.
const formatCompact = (num, digits) => {
    num = Number(num); // Ensure num is a valid number
    const appliedDigits = digits !== undefined ? digits : 4;

    if (isNaN(num)) return ""; // Handle invalid numbers

    if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(appliedDigits) + "B"; // Billion
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(appliedDigits) + "M"; // Million
    if (num >= 1_000) return (num / 1_000).toFixed(appliedDigits) + "K"; // Thousand

    return num % 1 !== 0 ? num?.toFixed(appliedDigits) : num?.toString(); // Show decimals if needed
};

export const compactNumberWithTooltip = (amount, title = "", digits = 2) => {
    // Check if amount is a valid number
    if (isNaN(amount) || amount === "" || amount === null) {
        return <span></span>;  // Return an empty span if amount is invalid
    }
    const tooltipText = title ? `${title}: ${amount}` : `${amount}`;

    return (
        <WithTooltip tooltipText={tooltipText}>
            <span >
                {formatCompact(amount, digits)}
            </span>
        </WithTooltip>
    );
};

export const smallSwitchProps = {
    offColor: "#808080",
    onColor: "#0178C1",
    checkedIcon: false,
    uncheckedIcon: false,
    height: 12,
    width: 24
};
export const commonSwitchProps = {
    offColor: "#808080",
    onColor: "#007cff",
    checkedIcon: false,
    uncheckedIcon: false,
    height: 18,
    width: 36
};
export function getSubPnlComponent(pnlVal) {
    pnlVal = Number(parseFloat(pnlVal)?.toFixed(2));
    let colorClass;
    let icon;

    // Determine the color class and icon based on the value
    if (pnlVal < 0) {
        colorClass = 'text-danger';
        icon = <i className="mdi mdi-arrow-down"></i>;
    } else if (pnlVal === 0.0 || pnlVal === null) {
        pnlVal = 0.0;
        icon = null; // No icon for zero or null values
    } else if (pnlVal > 0) {
        colorClass = 'text-success';
        icon = <i className="mdi mdi-arrow-up"></i>;
    }

    // Return the original pnlVal with color and icon, without formatting or tooltip
    return (
        <span className={colorClass}>
            {pnlVal}{icon}
        </span>
    );
}

export function getTransactionType(transaction) {
    let a_type = transaction?.charAt(0);
    let is_buy =
        a_type === "B" ||
        a_type === "b" ||
        a_type === "buy" ||
        a_type === "BUY" ||
        a_type === "Buy";
    let is_sell =
        a_type === "S" ||
        a_type === "s" ||
        a_type === "sell" ||
        a_type === "SELL" ||
        a_type === "Sell";
    if (is_buy) {
        return <label className="custom-badge success" style={{ fontSize: "14px" }}>Buy</label>
    } else if (is_sell) {
        return <label className="custom-badge danger" style={{ fontSize: "14px" }}>Sell</label>
    } else {
        return (
            transaction && (
                <label className="badge badge-outline-danger">
                    {transaction.toUpperCase()}
                </label>
            )
        );
    }
}
export const getIpStatusBadgeClass = (status) => {
    switch (status) {
        case STATIC_IP_STATUS.AVAILABLE:
            return "custom-badge primary px-2 p-1";
        case STATIC_IP_STATUS.CONNECTED:
            return "custom-badge success px-2 p-1";
        case STATIC_IP_STATUS.EXPIRED:
            return "custom-badge warning px-2 p-1";
        case STATIC_IP_STATUS.DELETED:
            return "custom-badge danger px-2 p-1";
        default:
            return "custom-badge info px-2 p-1"; // fallback
    }
};
//  Reusable Badge Component
export function getBadge(type, text) {
    return <span className={`custom-badge ${type}`}>{text}</span>;
}

//  Status Badge with Tooltip
export function getStatusBadge(status, reason) {
    const tooltipText = `${status}${(status === ORDER_STATUS.REJECTED || status === ORDER_STATUS.FAILED) && reason ? ` - ${reason}` : ""}`;
    const badgeType =
        status === ORDER_STATUS.COMPLETD
            ? "success"
            : status === ORDER_STATUS.PENDING
                ? "warning"
                : "danger";

    return (
        <WithTooltip tooltipText={tooltipText}>
            {getBadge(badgeType, status?.charAt(0).toUpperCase())}
        </WithTooltip>
    );
}

export const datePickerStyles = (isDarkTheme) => ({
    textField: {
        fullWidth: true,

        InputLabelProps: {

            style: {
                color: isDarkTheme ? "#fff" : "#acb0c3", // 🔹 Placeholder text color
                width: "100%", // 🔹 Ensure full width for alignment

            },
        },
        InputProps: {
            style: {
                color: isDarkTheme ? "#fff" : "#000", // 
                backgroundColor: isDarkTheme ? "#2a3038" : "#fff",
                border: `1px solid ${isDarkTheme ? "#2c2e33" : "#acb0c3"}`,
                borderRadius: "8px",
                height: "40px",
                fontSize: "14px",
                textAlign: "center",
                "&.Mui-disabled": {
                    color: "#ccc !important",
                },
            },

        },
    },
    ampmInClock: true,
    dialog: {
        sx: {
            "& .MuiPickersDay-root.Mui-disabled": {
                color: "#aba7a7 !important",
            },
            "& .Mui-disabled": {
                color: "#aba7a7 !important", // 🔹 General disabled text color
            },
            "& .MuiTypography-root.Mui-disabled": {
                color: "#aba7a7 !important", // 🔹 Ensure disabled typography changes
            },
            "& .MuiPaper-root": {
                backgroundColor: isDarkTheme ? "#222" : "#fff", // 🔹 Popup background
                color: isDarkTheme ? "#fff" : "#000", // 🔹 Text color
            },
            "& .MuiPickersToolbar-root": {
                backgroundColor: isDarkTheme ? "#444" : "#ddd", // 🔹 Toolbar background
                color: "#fff", // 🔹 Toolbar text color
            },
            "& .MuiTypography-root": {
                color: isDarkTheme ? "#fff" : "#000", // 🔹 Ensures all text inside picker is white
            },
            "& .MuiButtonBase-root": {
                color: isDarkTheme ? "#fff" : "#000", // 🔹 Button text color
            },
            "& .MuiSvgIcon-root": {
                color: isDarkTheme ? "#fff" : "#000", // 🔹 Icons color
            },
            "& .MuiPickersYear-root, & .MuiPickersCalendarHeader-root, & .MuiDayCalendar-header, & .MuiTypography-h4": {
                color: isDarkTheme ? "#fff" : "#000", // 🔹 Year, Month, Day header colors
            },
            "& .MuiClockNumber-root": {
                color: isDarkTheme ? "#fff" : "#000", // 🔹 Clock numbers color
            },

            "& .MuiPickersToolbarText-root.Mui-selected": {
                color: "#1976d2 !important",
                fontWeight: "bold !important",
            },
            "& .MuiPickersToolbarText-root": {
                color: "#fff !important", // 🔸 Unselected AM/PM text color
            },
        },
    },
});
export const formatNumberWithTooltip = (amount) => {
    // Check if amount is a valid number
    if (isNaN(amount) || amount === "" || amount === null) {
        return <span></span>;  // Return an empty span if amount is invalid
    }
    // Format the amount to 2 decimal places
    const formattedAmount = parseFloat(amount).toFixed(2);

    // Return a span with the full amount in the tooltip (title)
    return (
        <WithTooltip tooltipText={amount}>
            <span >
                {formattedAmount}
            </span>
        </WithTooltip>
    );
};